import { Component } from '@theme/component';

/**
 * Countdown Timer Component for Announcement Bars
 * Displays a countdown to a specific date/time with customizable formatting
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 * @property {HTMLElement} [daysLabel]
 * @property {HTMLElement} [hoursLabel]
 * @property {HTMLElement} [minutesLabel]
 * @property {HTMLElement} [secondsLabel]
 * @property {HTMLElement} [expiredMessage]
 * @property {HTMLElement} [countdownWrapper]
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  /** @type {number|undefined} */
  #interval = undefined;
  /** @type {Date|null} */
  #endDate = null;

  connectedCallback() {
    super.connectedCallback();
    
    const endDateStr = this.getAttribute('end-date');
    const endTimeStr = this.getAttribute('end-time') || '23:59';
    
    if (endDateStr) {
      this.#endDate = new Date(`${endDateStr}T${endTimeStr}:00`);
      this.#startCountdown();
    }
  }

  disconnectedCallback() {
    this.#stopCountdown();
    super.disconnectedCallback?.();
  }

  #startCountdown() {
    // Update immediately
    this.#updateCountdown();
    
    // Then update every second
    this.#interval = setInterval(() => {
      this.#updateCountdown();
    }, 1000);
  }

  #stopCountdown() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  #updateCountdown() {
    if (!this.#endDate) return;

    const now = new Date();
    const diff = this.#endDate.getTime() - now.getTime();

    if (diff <= 0) {
      this.#handleExpired();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.#updateElement('days', days);
    this.#updateElement('hours', hours);
    this.#updateElement('minutes', minutes);
    this.#updateElement('seconds', seconds);

    // Update labels for singular/plural
    this.#updateLabel('daysLabel', days, 'd', 'd');
    this.#updateLabel('hoursLabel', hours, 'h', 'h');
    this.#updateLabel('minutesLabel', minutes, 'm', 'm');
    this.#updateLabel('secondsLabel', seconds, 's', 's');
  }

  /**
   * @param {string} refName
   * @param {number} value
   */
  #updateElement(refName, value) {
    const element = /** @type {HTMLElement|undefined} */ (this.refs[refName]);
    if (element) {
      element.textContent = String(value).padStart(2, '0');
    }
  }

  /**
   * @param {string} refName
   * @param {number} value
   * @param {string} singular
   * @param {string} plural
   */
  #updateLabel(refName, value, singular, plural) {
    const element = /** @type {HTMLElement|undefined} */ (this.refs[refName]);
    if (element) {
      element.textContent = value === 1 ? singular : plural;
    }
  }

  #handleExpired() {
    this.#stopCountdown();
    
    const expiredAction = this.getAttribute('expired-action') || 'show-message';
    
    if (expiredAction === 'hide') {
      // Hide the entire announcement bar or just this countdown
      const announcement = /** @type {HTMLElement|null} */ (this.closest('.announcement-bar__slide') || this.closest('.announcement-bar'));
      if (announcement) {
        announcement.style.display = 'none';
      }
    } else if (expiredAction === 'show-message') {
      // Show expired message
      const wrapper = /** @type {HTMLElement|undefined} */ (this.refs.countdownWrapper);
      const expired = /** @type {HTMLElement|undefined} */ (this.refs.expiredMessage);
      if (wrapper) {
        wrapper.style.display = 'none';
      }
      if (expired) {
        expired.style.display = 'inline';
      }
    }
    // If 'keep-showing', just stop updating but keep displaying last values
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
